import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Form, InputGroup, ListGroup, Spinner, Badge, Button } from "react-bootstrap";
import DataApi from "../../api/DataApi";
import { resolveEffectiveLookupFilterLogic } from "../../utils/lookupFilterUtils";

const MultiLookupLayout = ({
  objectName,
  searchFields,
  displayFields,
  selectedValues = [], // Array of {id, label} objects
  onChange,
  disabled,
  placeholder = "Search...",
  isRequired = false,
  lookupFilter = null,
  lookupFilterLogic = 'AND',
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownIdRef = useRef(`multilookup-dropdown-${objectName}-${Date.now()}-${Math.random()}`);

  const searchKeys = searchFields?.split(",").map((f) => f.trim()) || [];
  const displayKeys = displayFields
    ? displayFields.split(",").map((f) => f.trim())
    : searchKeys;

  // Calculate dropdown position when it should be shown
  useEffect(() => {
    if (showDropdown && suggestions.length > 0 && inputRef.current) {
      const updatePosition = () => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showDropdown, suggestions.length]);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        const dropdownElement = document.getElementById(dropdownIdRef.current);
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setShowDropdown(false);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        const dataApi = new DataApi(`object/lookup/${objectName}`);

        const searchParams = {
          q: searchTerm,
          fields: searchFields,
          display_fields: displayFields,
        };

        // Convert lookup_filter array to filters array format with logic
        let filtersArray = [];
        if (lookupFilter && Array.isArray(lookupFilter) && lookupFilter.length > 0) {
          filtersArray = lookupFilter
            .filter(filter => filter.field && filter.operator && filter.value !== undefined && filter.value !== null && filter.value !== '')
            .map(filter => {
              let filterValue = filter.value;
              if (filter.operator === 'in' || filter.operator === 'not_in') {
                const values = String(filter.value).split(',').map(v => v.trim()).filter(Boolean);
                filterValue = values.length === 1 ? values[0] : values;
              }
              return {
                field: filter.field,
                operator: filter.operator,
                value: filterValue
              };
            });
        }

        if (filtersArray.length === 0 && objectName === 'role') {
          filtersArray = [{ field: 'is_active', operator: 'equals', value: 'true' }];
        }

        if (filtersArray.length > 0) {
          searchParams.filters = JSON.stringify(filtersArray);
          searchParams.filterLogic = resolveEffectiveLookupFilterLogic(lookupFilterLogic);
        }

        const res = await dataApi.search(searchParams);

        // Filter out already selected items
        const selectedIds = selectedValues.map(v => v.id || v);
        const dataWithLabels = (res.data || [])
          .filter(item => !selectedIds.includes(item.id))
          .map((item) => ({
            ...item,
            label: displayKeys
              .map((key) => item[key]?.trim?.() || "")
              .filter(Boolean)
              .join(" "),
          }));

        setSuggestions(dataWithLabels);
      } catch (err) {
        console.error("MultiLookup error", err);
      } finally {
        setLoading(false);
        setShowDropdown(true);
      }
    };

    const delayDebounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, objectName, searchFields, displayFields, lookupFilter, lookupFilterLogic, selectedValues]);

  const handleSelect = (item) => {
    const newSelected = [...selectedValues, { id: item.id, label: item.label }];
    onChange(newSelected);
    setSearchTerm("");
    setShowDropdown(false);
  };

  const handleRemove = (idToRemove) => {
    const newSelected = selectedValues.filter(v => (v.id || v) !== idToRemove);
    onChange(newSelected);
  };

  return (
    <div style={{ position: "relative" }} ref={inputRef}>
      {/* Search input */}
      <InputGroup>
        <Form.Control
          type="text"
          value={searchTerm}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            if (searchTerm.length >= 2) setShowDropdown(true);
          }}
          required={isRequired}
          style={{
            height: "calc(1.5em + 0.75rem + 2px)",
            minHeight: "38px",
            fontSize: "1rem",
            padding: "0.375rem 0.75rem"
          }}
        />
        {loading && (
          <InputGroup.Text>
            <Spinner animation="border" size="sm" />
          </InputGroup.Text>
        )}
      </InputGroup>

      {/* Selected values display - below search box */}
      {selectedValues.length > 0 && (
        <div className="mt-2" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {selectedValues.map((selected, idx) => (
            <Badge
              key={selected.id || idx}
              bg="primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                fontSize: '0.875rem'
              }}
            >
              <span>{selected.label || selected.id}</span>
              <Button
                variant="link"
                size="sm"
                onClick={() => handleRemove(selected.id || selected)}
                disabled={disabled}
                style={{
                  color: '#fff',
                  padding: 0,
                  margin: 0,
                  fontSize: '0.875rem',
                  lineHeight: 1,
                  textDecoration: 'none'
                }}
              >
                ×
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Dropdown suggestions */}
      {showDropdown && suggestions.length > 0 && !disabled && typeof document !== 'undefined' && createPortal(
        <ListGroup
          id={dropdownIdRef.current}
          style={{
            position: "fixed",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 9999,
            maxHeight: 200,
            overflowY: "auto",
            background: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            borderRadius: "4px",
            border: "1px solid #ccc",
            marginTop: "2px",
          }}
        >
          {suggestions.map((item) => (
            <ListGroup.Item
              action
              key={item.id}
              onClick={() => handleSelect(item)}
              style={{ cursor: "pointer" }}
            >
              {item.label}
            </ListGroup.Item>
          ))}
        </ListGroup>,
        document.body
      )}
    </div>
  );
};

export default MultiLookupLayout;
